import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useMultiAccountStore } from '../stores/multiAccountStore';
import { isTokenExpired } from '@billda/shared-ui';

/**
 * Hook to initialize authentication on app load.
 * Checks for a stored account and refreshes the token if valid.
 * Should be called once at the app root.
 *
 * Returns true when initialization is complete (either authenticated or not).
 */
export const useAuthInitialization = (): boolean => {
  const hasInitialized = useRef(false);
  const [isReady, setIsReady] = useState(false);
  const navigate = useNavigate();
  const refreshAccessToken = useAuthStore(state => state.refreshAccessToken);
  const getCurrentAccount = useMultiAccountStore(
    state => state.getCurrentAccount
  );
  const setCurrentAccount = useMultiAccountStore(
    state => state.setCurrentAccount
  );

  useEffect(() => {
    // Only run once
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const initialize = async () => {
      console.log('🔵 useAuthInitialization: Starting initialization');

      // Migration: Clean up old auth-storage from localStorage and sessionStorage
      try {
        const oldAuthStorage = localStorage.getItem('auth-storage');
        if (oldAuthStorage) {
          console.log('🔄 Cleaning up old auth-storage from localStorage');
          localStorage.removeItem('auth-storage');
        }
        const sessionAuthStorage = sessionStorage.getItem('auth-storage');
        if (sessionAuthStorage) {
          console.log('🔄 Cleaning up old auth-storage from sessionStorage');
          sessionStorage.removeItem('auth-storage');
        }
      } catch (e) {
        console.error('Error during auth-storage cleanup:', e);
      }

      // Check multiAccountStore contents
      const allAccounts = useMultiAccountStore.getState().getAllAccounts();
      console.log('🔵 All stored accounts:', allAccounts);

      // Check for current account with refresh token
      let currentAccount = getCurrentAccount();
      console.log('🔵 Current account:', currentAccount);

      // If no current account but we have stored accounts, select the most recent one
      if (!currentAccount && allAccounts.length > 0) {
        console.log(
          '🔵 No current account selected, auto-selecting most recent account'
        );
        const mostRecent = allAccounts[0]; // Already sorted by lastUsedAt
        setCurrentAccount(mostRecent.email);
        currentAccount = mostRecent;
        console.log('🔵 Auto-selected account:', currentAccount.email);
      }

      if (currentAccount?.refreshToken) {
        const expired = isTokenExpired(currentAccount.refreshToken);

        if (expired === false) {
          // Refresh token is valid, refresh the access token
          console.log(
            '🔄 Restoring authentication from stored account - refreshing token now'
          );

          try {
            const success = await refreshAccessToken();
            if (success) {
              console.log('✅ Session restored successfully');

              // Redirect to root if currently on a public/auth page
              const publicPaths = ['/login'];
              const currentPath = window.location.pathname;
              const isOnPublicPage = publicPaths.some(
                path =>
                  currentPath === path || currentPath.startsWith(path + '/')
              );

              if (isOnPublicPage) {
                console.log(`🔵 Redirecting from ${currentPath} to root`);
                navigate('/', { replace: true });
              }
            } else {
              console.log('❌ Token refresh failed during initialization');
            }
          } catch (error) {
            console.log('❌ Token refresh error during initialization:', error);
          }
        } else {
          // Refresh token expired, clear current account
          console.log(
            '🔄 Stored refresh token expired - clearing authentication'
          );
          setCurrentAccount(null);
        }
      } else {
        console.log('🔵 No current account found or no refresh token');
      }

      // Mark as ready
      console.log('🔵 Initialization complete, setting isReady = true');
      setIsReady(true);
    };

    initialize();
  }, [refreshAccessToken, getCurrentAccount, setCurrentAccount, navigate]);

  return isReady;
};
